/**
 * Types and constants for task field change reasons.
 * Used when users modify tracked fields to capture the reason for the change.
 *
 * Currently simplified to just "Other" - we'll add specific reasons
 * once we learn what's most common from usage patterns.
 */

import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Fields that are tracked for change reasons.
 * Backend may track: due_date, status, assignee
 */
export type ChangeField = 'due_date' | 'status' | 'assignee';

/**
 * Reason for a field change. Currently just 'other' - extend as needed.
 * Backend accepts any string, so this type is for frontend presets only.
 */
export type TaskFieldChangeReason = 'other';

interface ReasonOption {
  value: TaskFieldChangeReason;
  label: string;
}

// =============================================================================
// TREATMENT CHECK-IN SPEEDBUMP
// =============================================================================

export const TREATMENT_CHECK_IN_STATUS_WARNING =
  "This task should remain open until the client's treatment process has been completed fully and no future check-ins are required.";

export const TREATMENT_CHECK_IN_PROTECTED_STATUSES: readonly CanonicalTaskStatus[] =
  [CanonicalTaskStatus.COMPLETED, CanonicalTaskStatus.NOT_APPLICABLE] as const;

export interface TreatmentCheckInCheckbox {
  id: string;
  label: string;
}

export const TREATMENT_CHECK_IN_CHECKBOXES: readonly TreatmentCheckInCheckbox[] =
  [
    { id: 'records_received', label: 'All medical record tasks have been created.' },
    {
      id: 'treatment_confirmed',
      label: 'Treatment status has been confirmed with the client.',
    },
  ] as const;

export const isTreatmentCheckInProtectedStatus = (
  newStatus: string,
  canonicalTaskType?: string | null,
): boolean => {
  if (!canonicalTaskType) return false;
  if (canonicalTaskType !== CanonicalTaskType.TREATMENT_CHECK_IN) return false;
  return TREATMENT_CHECK_IN_PROTECTED_STATUSES.includes(
    newStatus as CanonicalTaskStatus,
  );
};

export const buildTreatmentCheckInComment = (userNote?: string): string => {
  const base =
    'User confirmed: records received; treatment status confirmed with client.';
  return userNote ? `${base} Note: ${userNote}` : base;
};

// =============================================================================
// SPEEDBUMP CONFIGURATION
// =============================================================================

/**
 * Configuration for when a change reason modal ("speedbump") is required.
 *
 * Each field type has its own configuration that determines:
 * - Whether the speedbump is enabled for that field
 * - Under what conditions the speedbump should trigger
 *
 * @example
 * // Check if status change requires reason
 * const config = FIELD_CHANGE_SPEEDBUMP_CONFIG.status;
 * if (config.enabled && config.requiresReason({ newValue: 'Blocked' })) {
 *   // Show modal
 * }
 */
export interface SpeedbumpFieldConfig<TContext = unknown> {
  /** Whether speedbump is enabled for this field type */
  enabled: boolean;
  /** Function that determines if a specific change requires a reason */
  requiresReason: (context: TContext) => boolean;
}

/** Context for status change speedbump decisions */
export interface StatusChangeContext {
  oldValue: string | null;
  newValue: string;
}

/** Context for due date change speedbump decisions */
export interface DueDateChangeContext {
  oldValue: string | null;
  newValue: string;
}

/** Context for assignee change speedbump decisions */
export interface AssigneeChangeContext {
  oldValue: string | null;
  newValue: string | null;
}

/**
 * Statuses that require a reason when changed TO them.
 * These are typically terminal or blocking states where we want
 * to capture why the task reached this state.
 */
export const STATUSES_REQUIRING_REASON: readonly CanonicalTaskStatus[] = [
  CanonicalTaskStatus.BLOCKED,
  CanonicalTaskStatus.NOT_APPLICABLE,
  CanonicalTaskStatus.CLOSED,
] as const;

/**
 * Centralized configuration for field change speedbumps.
 *
 * This config-driven approach:
 * - Keeps all speedbump logic in one place
 * - Makes it easy to add new fields or modify rules
 * - Provides type safety for each field's context
 * - Documents the business rules clearly
 *
 * @see docs/TASK_FIELD_CHANGE_TRACKING.md for architecture details
 */
export const FIELD_CHANGE_SPEEDBUMP_CONFIG = {
  /**
   * Status field: Requires reason when changing to BLOCKED or NOT_APPLICABLE.
   * These states indicate the task cannot proceed or is irrelevant,
   * so we capture why for audit and reporting purposes.
   */
  status: {
    enabled: true,
    requiresReason: ({ newValue }: StatusChangeContext): boolean => {
      return STATUSES_REQUIRING_REASON.includes(
        newValue as CanonicalTaskStatus,
      );
    },
  } satisfies SpeedbumpFieldConfig<StatusChangeContext>,

  /**
   * Due date field: Requires reason when CHANGING an existing due date.
   * Setting initial due date doesn't require a reason, but modifications do.
   * This helps track why deadlines are being adjusted.
   */
  due_date: {
    enabled: true,
    requiresReason: ({ oldValue }: DueDateChangeContext): boolean => {
      // Only require reason if there was a previous due date
      return oldValue !== null && oldValue !== '';
    },
  } satisfies SpeedbumpFieldConfig<DueDateChangeContext>,

  /**
   * Assignee field: Currently disabled.
   * Can be enabled in the future if we need to track reassignment reasons.
   */
  assignee: {
    enabled: false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- context reserved for future use
    requiresReason: (_context: AssigneeChangeContext): boolean => false,
  } satisfies SpeedbumpFieldConfig<AssigneeChangeContext>,
} as const;

// Type for the config object
export type FieldChangeSpeedbumpConfig = typeof FIELD_CHANGE_SPEEDBUMP_CONFIG;

// =============================================================================
// SPEEDBUMP HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a status change requires a reason.
 * Convenience function that wraps the config for common use case.
 *
 * @param newStatus - The status being changed to
 * @param oldStatus - The current status (optional)
 * @returns true if the modal should be shown
 *
 * @example
 * if (requiresStatusChangeReason('Blocked')) {
 *   const result = await requestChangeReason({ ... });
 * }
 */
export const requiresStatusChangeReason = (
  newStatus: string,
  oldStatus?: string | null,
): boolean => {
  const config = FIELD_CHANGE_SPEEDBUMP_CONFIG.status;
  if (!config.enabled) return false;
  return config.requiresReason({
    oldValue: oldStatus ?? null,
    newValue: newStatus,
  });
};

/**
 * Check if a due date change requires a reason.
 * Convenience function that wraps the config for common use case.
 *
 * @param currentDueDate - The task's current due date (null if not set)
 * @param newDueDate - The new due date being set
 * @returns true if the modal should be shown
 *
 * @example
 * if (requiresDueDateChangeReason(task.due_date, newDate)) {
 *   const result = await requestChangeReason({ ... });
 * }
 */
export const requiresDueDateChangeReason = (
  currentDueDate: string | null | undefined,
  newDueDate: string,
): boolean => {
  const config = FIELD_CHANGE_SPEEDBUMP_CONFIG.due_date;
  if (!config.enabled) return false;
  return config.requiresReason({
    oldValue: currentDueDate ?? null,
    newValue: newDueDate,
  });
};

/**
 * Check if an assignee change requires a reason.
 * Currently always returns false as assignee speedbump is disabled.
 *
 * @param currentAssignee - The task's current assignee ID
 * @param newAssignee - The new assignee ID
 * @returns true if the modal should be shown
 */
export const requiresAssigneeChangeReason = (
  currentAssignee: string | null | undefined,
  newAssignee: string | null,
): boolean => {
  const config = FIELD_CHANGE_SPEEDBUMP_CONFIG.assignee;
  if (!config.enabled) return false;
  return config.requiresReason({
    oldValue: currentAssignee ?? null,
    newValue: newAssignee,
  });
};

// =============================================================================
// BULK OPERATION HELPERS
// =============================================================================

/**
 * Check if a bulk status change requires a reason.
 * For bulk operations: always require reason when changing to BLOCKED or NOT_APPLICABLE.
 *
 * @param newStatus - The canonical status being changed to
 * @returns true if the modal should be shown
 */
export const requiresBulkStatusChangeReason = (newStatus: string): boolean => {
  return STATUSES_REQUIRING_REASON.includes(newStatus as CanonicalTaskStatus);
};

/**
 * Check if a bulk due date change requires a reason.
 * For bulk operations: always require reason for any due date change.
 *
 * @returns true (always requires reason for bulk due date changes)
 */
export const requiresBulkDueDateChangeReason = (): boolean => {
  return true;
};

// =============================================================================
// FIELD DISPLAY CONFIG
// =============================================================================

/**
 * Display labels for each tracked field type.
 * Used in modals, timeline items, and other UI surfaces.
 */
export const FIELD_DISPLAY_CONFIG: Record<
  ChangeField,
  { label: string; nullLabel: string }
> = {
  status: { label: 'Status', nullLabel: 'None' },
  due_date: { label: 'Due Date', nullLabel: 'None' },
  assignee: { label: 'Assignee', nullLabel: 'Unassigned' },
};

/**
 * Get the display label for a field type.
 */
export const getFieldLabel = (field: ChangeField): string =>
  FIELD_DISPLAY_CONFIG[field].label;

/**
 * Get the null/empty display label for a field type.
 */
export const getFieldNullLabel = (field: ChangeField): string =>
  FIELD_DISPLAY_CONFIG[field].nullLabel;

// =============================================================================
// REASON OPTIONS
// =============================================================================

/**
 * Reason options shown in the modal.
 * Same for all field types until we identify field-specific patterns.
 */
const REASON_OPTIONS: ReadonlyArray<ReasonOption> = [
  { value: 'other', label: 'Other' },
] as const;

/**
 * Get reason options for a field type.
 * Currently returns the same options for all fields.
 * The field parameter is kept for future extensibility when
 * we add field-specific reason presets.
 */
export const getReasonOptionsForField = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Reserved for future field-specific options
  field: ChangeField,
): ReadonlyArray<ReasonOption> => {
  // TODO: Return field-specific options once we identify patterns
  // e.g., if (field === 'due_date') return DUE_DATE_REASON_OPTIONS;
  return REASON_OPTIONS;
};

/**
 * Get display label for a change reason.
 * Used by FieldChangeUpdate to display the reason in the updates stream.
 * Falls back to the raw reason string if not found in presets.
 */
export const getChangeReasonLabel = (reason: string): string => {
  const option = REASON_OPTIONS.find((o) => o.value === reason);
  return option?.label ?? reason;
};
